"use client";
import { useEffect, useState } from "react";
import PrivateRoute from "@/components/PrivateRoute";
import AdminLayout from "@/components/layout/AdminLayout";
import apiClient, { getGlobalPlatformFeeStats } from "@/lib/apiClient";
import { FiUsers, FiHome, FiShoppingCart, FiDollarSign, FiActivity, FiMessageSquare, FiMail } from "react-icons/fi";
import { FaLine } from "react-icons/fa";

interface SystemStats {
  companies_count: number;
  schools_count: number;
  orders_count: number;
  total_sales: number;
  total_balance: number;
  total_withdrawn: number;
  notifications: {
    in_app: number;
    email: number;
    line: number;
  };
}

export default function HomePage() {
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [platformFees, setPlatformFees] = useState<{ total_balance: number; total_withdrawn: number } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsData, feeData] = await Promise.all([
        apiClient("/api/v1/system/stats"),
        getGlobalPlatformFeeStats()
      ]);
      setStats(statsData);
      setPlatformFees(feeData);
    } catch (error) {
      console.error("Failed to fetch dashboard data", error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, subValue }: { title: string, value: string | number, icon: any, color: string, subValue?: string }) => (
    <div className="bg-white overflow-hidden rounded-lg shadow">
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <Icon className={`h-6 w-6 ${color}`} aria-hidden="true" />
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="truncate text-sm font-medium text-gray-500">{title}</dt>
              <dd>
                <div className="text-lg font-medium text-gray-900">{value}</div>
                {subValue && <div className="text-xs text-gray-400 mt-1">{subValue}</div>}
              </dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('zh-TW', { style: 'currency', currency: 'TWD', maximumFractionDigits: 0 }).format(amount);
  };

  return (
    <PrivateRoute>
      <AdminLayout>
        <div className="space-y-8">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">系統管理儀表板</h1>
            <p className="text-gray-400">全系統核心指標與財務概況</p>
          </div>

          {loading ? (
            <div className="text-center py-12 text-gray-400">載入中...</div>
          ) : (
            <>
              {/* 核心指標 */}
              <div>
                <h3 className="text-lg font-medium leading-6 text-gray-200 mb-4">核心運營指標</h3>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                  <StatCard
                    title="總公司數"
                    value={stats?.companies_count || 0}
                    icon={FiUsers}
                    color="text-blue-500"
                  />
                  <StatCard
                    title="總學院數"
                    value={stats?.schools_count || 0}
                    icon={FiHome}
                    color="text-indigo-500"
                  />
                  <StatCard
                    title="總訂單數"
                    value={stats?.orders_count || 0}
                    icon={FiShoppingCart}
                    color="text-green-500"
                  />
                  <StatCard
                    title="全平台 GMV (總交易額)"
                    value={formatCurrency(stats?.total_sales || 0)}
                    icon={FiActivity}
                    color="text-purple-500"
                  />
                </div>
              </div>

              {/* 財務指標 */}
              <div>
                <h3 className="text-lg font-medium leading-6 text-gray-200 mb-4">財務狀況</h3>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                  <StatCard
                    title="平台手續費收入 (系統淨利)"
                    value={formatCurrency(platformFees?.total_balance || 0)}
                    subValue={`已提領: ${formatCurrency(platformFees?.total_withdrawn || 0)}`}
                    icon={FiDollarSign}
                    color="text-yellow-500"
                  />
                  <StatCard
                    title="學院錢包總餘額 (代管資金)"
                    value={formatCurrency(stats?.total_balance || 0)}
                    icon={FiDollarSign}
                    color="text-blue-400"
                  />
                  <StatCard
                    title="學院已提領總額"
                    value={formatCurrency(stats?.total_withdrawn || 0)}
                    icon={FiDollarSign}
                    color="text-teal-400"
                  />
                </div>
              </div>

              {/* 通知統計 */}
              <div>
                <h3 className="text-lg font-medium leading-6 text-gray-200 mb-4">通知系統用量</h3>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                  <StatCard
                    title="站內通知發送"
                    value={stats?.notifications.in_app || 0}
                    icon={FiMessageSquare}
                    color="text-pink-500"
                  />
                  <StatCard
                    title="Email 發送"
                    value={stats?.notifications.email || 0}
                    icon={FiMail}
                    color="text-red-500"
                  />
                  <StatCard
                    title="LINE 通知發送"
                    value={stats?.notifications.line || 0}
                    icon={FaLine}
                    color="text-green-600"
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </AdminLayout>
    </PrivateRoute>
  );
}
